import { Injectable, computed, inject } from '@angular/core';
import { StateService } from './state.service';
import { User } from '../../auth/user.model';
import { UserProfile } from '../../models/user-profile.model';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { NotificationStateService } from './notification.state';
import { EMPTY, firstValueFrom } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { StateCleaner } from './state-cleaner.service';
import { ApiService } from '../services/api.service';
import { ErrorHandlerService } from '../services/error-handler.service';
import { OidcSecurityService } from 'angular-auth-oidc-client';

export type SessionStatus = 'unknown' | 'ready' | 'onboarding_required' | 'unauthenticated';

export interface SessionProfile {
  id: number;
  email: string;
  first_name: string | null;
  last_name: string | null;
  username: string;
}

export interface OnboardingClaims {
  email: string | null;
  first_name: string | null;
  last_name: string | null;
}

export interface ActivationStatus {
  state: 'activation_pending';
  email: string;
  username: string;
}

export interface PaymentMethod {
  id: string;
  type: string;
  card?: {
    brand: string;
    last4: string;
    exp_month: number;
    exp_year: number;
  };
  billing_details: {
    name: string;
    email: string;
    phone: string;
    address: {
      city: string;
      country: string;
      line1: string;
      line2: string;
      postal_code: string;
      state: string;
    };
  };
}

export interface UserState {
  currentUser: User | null;
  profile: UserProfile | null;
  sessionStatus: SessionStatus;
  onboardingClaims: OnboardingClaims | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  paymentMethods: {
    data: PaymentMethod[];
  };
  setupIntent: {
    client_secret: string;
  } | null;
  isLoadingPaymentMethods: boolean;
  isLoadingSetupIntent: boolean;
}

const initialUserState: UserState = {
  currentUser: null,
  profile: null,
  sessionStatus: 'unknown',
  onboardingClaims: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
  paymentMethods: {
    data: [],
  },
  setupIntent: null,
  isLoadingPaymentMethods: false,
  isLoadingSetupIntent: false,
};

@Injectable({
  providedIn: 'root',
})
export class UserStateService extends StateService<UserState> {
  private http = inject(HttpClient);
  private notificationService = inject(NotificationStateService);
  private stateCleaner = inject(StateCleaner);
  private apiService = inject(ApiService);
  private errorHandler = inject(ErrorHandlerService);
  private oidcSecurityService = inject(OidcSecurityService);

  // Computed signals for derived state
  readonly isAuthenticated = computed(() => !!this.state().currentUser);
  readonly isLoading = this.selectSlice(state => state.isLoading);
  readonly currentUser = this.selectSlice(state => state.currentUser);
  readonly profile = this.selectSlice(state => state.profile);
  readonly sessionStatus = this.selectSlice(state => state.sessionStatus);
  readonly onboardingClaims = this.selectSlice(state => state.onboardingClaims);
  readonly error = this.selectSlice(state => state.error);
  readonly paymentMethods = this.selectSlice(state => state.paymentMethods);
  readonly setupIntent = this.selectSlice(state => state.setupIntent);
  readonly isLoadingPaymentMethods = this.selectSlice(state => state.isLoadingPaymentMethods);
  readonly isLoadingSetupIntent = this.selectSlice(state => state.isLoadingSetupIntent);

  // Computed signals for derived data
  readonly isDemoAccount = computed(() => {
    const user = this.currentUser();
    if (!user || !user.email) {
      return false;
    }
    return user.email.includes('demo-') && user.email.endsWith('@qlick.cloud');
  });

  constructor() {
    super(initialUserState);
    this.autoLogin();
  }

  onInit() {
    this.setUserProfile();

    // If user is authenticated, load payment methods
    if (this.isAuthenticated()) {
      this.loadPaymentMethods();
    }
  }

  /**
   * Redirects to the OIDC provider login page
   */
  loginWithRedirect(): void {
    this.oidcSecurityService.authorize();
  }

  logout(): void {
    // Clean all states before clearing user data
    this.stateCleaner.cleanAllStates();

    // Clean payment-related state
    this.cleanPaymentState();

    this.setState({ currentUser: null });

    this.oidcSecurityService.logoff().subscribe();
  }

  /**
   * Checks OIDC session and bootstraps session state from the API.
   * Skips on the callback URL — AuthCallbackComponent handles that flow.
   */
  autoLogin(): void {
    if (window.location.pathname === '/auth/callback' || window.location.pathname === '/activate') {
      return;
    }
    this.oidcSecurityService.checkAuth().subscribe(({ isAuthenticated, userData }) => {
      if (isAuthenticated && userData) {
        // Set currentUser immediately from token so isAuthenticated() is true
        // while bootstrapSession() resolves the full session status async.
        const username =
          userData['preferred_username'] || userData['email'] || userData['sub'] || '';
        const email = userData['email'] || '';
        this.setState({ currentUser: new User(username, email) });
        this.bootstrapSession();
      } else {
        this.stateCleaner.cleanAllStates();
        this.setState({ currentUser: null, sessionStatus: 'unauthenticated' });
      }
    });
  }

  /**
   * Bootstrap the session by calling GET /session.
   * Returns a promise that resolves to the session status.
   */
  bootstrapSession(): Promise<SessionStatus> {
    return new Promise(resolve => {
      this.http
        .get<{ state: string; profile: SessionProfile }>(this.apiService.getUrl('session'))
        .pipe(
          tap(response => {
            const p = response.profile;
            const user = new User(p.username, p.email);
            this.setState({
              sessionStatus: 'ready',
              currentUser: user,
              onboardingClaims: null,
            });
            resolve('ready');
          }),
          catchError((error: HttpErrorResponse) => {
            if (error.status === 409 && error.error?.detail?.state === 'onboarding_required') {
              const detail = error.error.detail;
              // Set a placeholder user so isAuthenticated() returns true
              const placeholderUser = new User('', detail.email || '');
              this.setState({
                sessionStatus: 'onboarding_required',
                currentUser: placeholderUser,
                onboardingClaims: {
                  email: detail.email,
                  first_name: detail.first_name,
                  last_name: detail.last_name,
                },
              });
              resolve('onboarding_required');
            } else {
              this.setState({ sessionStatus: 'unauthenticated' });
              resolve('unauthenticated');
            }
            return EMPTY;
          })
        )
        .subscribe();
    });
  }

  /**
   * Complete onboarding by submitting a username.
   * Returns a promise that resolves to true on success.
   */
  completeOnboarding(username: string): Promise<{ success: boolean; error?: string }> {
    return new Promise(resolve => {
      this.setState({ isLoading: true });
      this.http
        .post<{ state: string; profile: SessionProfile }>(
          this.apiService.getUrl('users/onboarding/complete'),
          { username }
        )
        .pipe(
          tap(response => {
            const p = response.profile;
            const user = new User(p.username, p.email);
            this.setState({
              sessionStatus: 'ready',
              currentUser: user,
              onboardingClaims: null,
              isLoading: false,
            });
            resolve({ success: true });
          }),
          catchError(error => {
            const msg = this.errorHandler.extractErrorMessage(error);
            this.setState({ isLoading: false, error: msg });
            resolve({ success: false, error: msg });
            return EMPTY;
          })
        )
        .subscribe();
    });
  }

  registerCustomer(data: {
    email: string;
    first_name: string;
    last_name: string;
    username: string;
  }): Promise<{ success: boolean; error?: string }> {
    return new Promise(resolve => {
      this.setState({ isLoading: true, error: null });
      this.http
        .post<{ state: string; email: string; message: string }>(
          this.apiService.getUrl('signup'),
          data
        )
        .pipe(
          tap(() => {
            this.setState({ isLoading: false });
            resolve({ success: true });
          }),
          catchError(error => {
            const msg = this.errorHandler.extractErrorMessage(error);
            this.setState({ isLoading: false, error: msg });
            resolve({ success: false, error: msg });
            return EMPTY;
          })
        )
        .subscribe();
    });
  }

  getActivationStatus(
    token: string
  ): Promise<{ success: boolean; data?: ActivationStatus; error?: string }> {
    return new Promise(resolve => {
      this.http
        .get<ActivationStatus>(
          this.apiService.getUrl(`activate/status?token=${encodeURIComponent(token)}`)
        )
        .pipe(
          tap(response => resolve({ success: true, data: response })),
          catchError(error => {
            const msg = this.errorHandler.extractErrorMessage(error);
            resolve({ success: false, error: msg });
            return EMPTY;
          })
        )
        .subscribe();
    });
  }

  async activateAccount(data: {
    token: string;
    password: string;
  }): Promise<{ success: boolean; redirectPath?: string; error?: string }> {
    this.setState({ isLoading: true, error: null });

    try {
      const activation = await firstValueFrom(
        this.http.post<{ state: string; redirect_path: string }>(
          this.apiService.getUrl('activate'),
          data
        )
      );

      this.setState({ isLoading: false });
      return { success: true, redirectPath: activation.redirect_path };
    } catch (error) {
      const msg = this.errorHandler.extractErrorMessage(error);
      this.setState({ isLoading: false, error: msg });
      return { success: false, error: msg };
    }
  }

  setUserProfile(): void {
    const profile = this.profile();

    if (profile) {
      return;
    }

    this.setState({ isLoading: true });

    const { currentUser } = this.getState();
    if (!currentUser) {
      this.setState({ isLoading: false, error: 'User not authenticated' });
      return;
    }

    this.http
      .get<UserProfile>(this.apiService.getUrl('users/me'))
      .pipe(
        tap(profile => {
          this.setState({ profile: profile, isLoading: false });

          // Load payment methods after profile is loaded
          if (profile.has_payment_method) {
            this.loadPaymentMethods();
          }
        }),
        catchError(error => {
          const msg = this.errorHandler.extractErrorMessage(error);
          this.setState({ isLoading: false, error: msg });
          this.notificationService.error(msg);
          return EMPTY;
        })
      )
      .subscribe();
  }

  updateUserProfile(profile: UserProfile): void {
    this.setState({ isLoading: true });

    const { currentUser } = this.getState();
    if (!currentUser) {
      this.setState({ isLoading: false, error: 'User not authenticated' });
      return;
    }

    const data: Record<string, any> = {
      user: {
        company: profile.company,
        tax_number: profile.tax_number,
        first_name: profile.first_name,
        last_name: profile.last_name,
        email: profile.email,
        username: profile.username,
      },
    };

    this.http
      .put(this.apiService.getUrl('users/me'), data)
      .pipe(
        tap(() => {
          this.setState({ profile: profile, isLoading: false });
          this.notificationService.success('Profile updated successfully');
        }),
        catchError(error => {
          const msg = this.errorHandler.extractErrorMessage(error);
          this.setState({ isLoading: false, error: msg });
          this.notificationService.error(msg);
          return EMPTY;
        })
      )
      .subscribe();
  }

  /**
   * Load payment methods
   */
  loadPaymentMethods(): void {
    this.setState({ isLoadingPaymentMethods: true, error: null });

    this.http
      .get<{ data: PaymentMethod[] }>(this.apiService.getUrl('payment_methods'))
      .pipe(
        tap(response => {
          this.setState({ paymentMethods: response, isLoadingPaymentMethods: false });
        }),
        catchError(error => {
          const msg = this.errorHandler.extractErrorMessage(error);
          this.setState({ isLoadingPaymentMethods: false, error: msg });
          this.notificationService.error(msg);
          return EMPTY;
        })
      )
      .subscribe();
  }

  /**
   * Create setup intent for adding a new payment method
   */
  createSetupIntent(): void {
    this.setState({ isLoadingSetupIntent: true, error: null });

    this.http
      .get<{ client_secret: string }>(this.apiService.getUrl('setup_intent'))
      .pipe(
        tap(setupIntent => {
          this.setState({ setupIntent, isLoadingSetupIntent: false });
        }),
        catchError(error => {
          const msg = this.errorHandler.extractErrorMessage(error);
          this.setState({ isLoadingSetupIntent: false, error: msg });
          this.notificationService.error(msg);
          return EMPTY;
        })
      )
      .subscribe();
  }

  /**
   * Add payment method
   */
  addPaymentMethod(paymentMethodId: string): void {
    this.setState({ isLoading: true, error: null });

    this.http
      .put<any>(this.apiService.getUrl('payment_methods'), {
        payment_method: paymentMethodId,
      })
      .pipe(
        tap(() => {
          this.setState({ isLoading: false });

          // Update profile to reflect payment method status
          if (this.profile()) {
            const updatedProfile = { ...this.profile()!, has_payment_method: true };
            this.setState({ profile: updatedProfile });
          }

          // Reload payment methods to get the updated list
          this.loadPaymentMethods();

          this.notificationService.success('Payment method added successfully');
        }),
        catchError(error => {
          const msg = this.errorHandler.extractErrorMessage(error);
          this.setState({ isLoading: false, error: msg });
          this.notificationService.error(msg);
          return EMPTY;
        })
      )
      .subscribe();
  }

  /**
   * Delete payment method
   */
  deletePaymentMethod(): void {
    this.setState({ isLoading: true, error: null });

    this.http
      .delete<any>(this.apiService.getUrl('payment_methods'))
      .pipe(
        tap(() => {
          this.setState({
            isLoading: false,
            paymentMethods: { data: [] },
          });

          // Update profile to reflect payment method status
          if (this.profile()) {
            const updatedProfile = { ...this.profile()!, has_payment_method: false };
            this.setState({ profile: updatedProfile });
          }

          this.notificationService.success('Payment method deleted successfully');
        }),
        catchError(error => {
          const msg = this.errorHandler.extractErrorMessage(error);
          this.setState({ isLoading: false, error: msg });
          this.notificationService.error(msg);
          return EMPTY;
        })
      )
      .subscribe();
  }

  /**
   * Clean payment-related state
   */
  cleanPaymentState(): void {
    this.setState({
      paymentMethods: { data: [] },
      setupIntent: null,
      isLoadingPaymentMethods: false,
      isLoadingSetupIntent: false,
    });
  }
}
